import { useEffect } from 'react';
import {
  createBrowserRouter,
  type LoaderFunctionArgs,
  redirect,
  type RouteObject,
} from 'react-router-dom';
import { BlacklistGuard } from '@hub/components/blacklist-guard';
import AccountDashboard from '@hub/features/account-dashboard';
import AffiliationRouteWrapper from '@hub/features/affiliation/affiliation-route-wrapper';
import AfterPurchase from '@hub/features/after-purchase/after-purchase';
import DashboardContainer from '@hub/features/dashboard-templates/dashboard-container';
import PaySafeCheckoutPage from '@hub/features/paysafe-checkout/paysafe-checkout-page';
import QuickPurchasePage from '@hub/features/quick-purchase/quick-purchase-page';
import { getRoot } from '@hub/helpers/getters.store';
import { useStore } from '@hub/helpers/use-store';
import { useFeatureFlags } from '@hub/hooks';
import {
  Achievements,
  AffiliateProgramPage,
  AffiliationWelcome,
  Article,
  BlacklistPage,
  Charts,
  CommunityStats,
  ContestCategoryPage,
  NovaContestDashboard,
  NovaContestPage,
  Overview,
  Partners,
  Playbook,
  PlaybookDetailPage,
  Portfolio,
  Position,
  PrivacyOptOut,
  Profile,
  PurchasePage,
  Purchases,
  PurchasesCoupons,
  PurchasesOrders,
  PurchasesWithdrawals,
  ReleasePreviewPage,
  TradeTalks,
  Welcome,
} from '@hub/routers/lazy-routes/lazy-private-components';
import { EnvService } from '@hub/services/core/env-service';
import {
  createAffiliationRoute,
  getNavigationRoutes,
} from '@hub/utils/router-utils';
import { useColorScheme } from '@mui/material/styles';
import {
  PagePath,
  StaticAffiliateKitUrls,
  StaticPagesUrls,
  Theme,
} from '@monorepo/client-types';
import {
  useFeatureFlagContext,
  useGetFeatureFlag,
} from '@monorepo/feature-flag/client';
import {
  createInitialRedirectLoader,
  getAndClearContestSignupFlag,
  redirectTo,
} from '@monorepo/hub-common';
import { StaticPage } from '@monorepo/react-components';
import {
  Application,
  CountryCodeType,
  FeatureFlagNames,
  Pages,
} from '@monorepo/types';
import Layout from '../../components/layout/layout';
import ErrorPage from '../../features/error-page/error-page';
import LogoutPage from '../../features/logout/logout';

/**
 * Blocks anonymous (mid-quick-purchase) users from accessing any Hub route,
 * redirecting them back to billing regardless of URL manipulation.
 *
 * currentUser is guaranteed to be hydrated here — this loader only runs inside
 * PrivateRouterProvider, which mounts only after userStore.getUser() resolves.
 */
const anonymousUserGuardLoader = () => {
  const { dataStore } = getRoot();
  const user = dataStore.userStore.currentUser;
  if (user?.isAnonymous) {
    return redirect(`${PagePath.Purchase}?step=billing&source=quick_purchase`);
  }
  return null;
};

export const overviewLoader = async () => {
  try {
    const { dataStore } = getRoot();
    const { accountStore } = dataStore;

    const { total } = await accountStore.paginate({
      page: 1,
      limit: 10,
      filters: accountStore.getOverviewFilters,
    });

    const hasNoAccounts = accountStore.isDefaultOverviewFilters && total === 0;
    if (!hasNoAccounts) return null;

    if (getAndClearContestSignupFlag()) {
      return redirect(PagePath.Contests);
    }
    return redirect(PagePath.Welcome);
  } catch (e) {
    console.error('Overview loader: Failed to check accounts', e);
    return null;
  }
};

// Dynamically select the correct URL (dark/light mode)
const AffiliateKitPage = () => {
  const { mode } = useColorScheme();
  const application = EnvService.application;

  const src =
    mode === Theme.Dark
      ? StaticAffiliateKitUrls['affiliate-kit-dark'][application]
      : StaticAffiliateKitUrls['affiliate-kit'][application];

  return <StaticPage src={src} />;
};

/**
 *
 */
export const usePrivateRoutes = (basename: string) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const {
    dataStore: { applicationConfigStore, userStore },
  } = useStore();

  const featureFlag = useFeatureFlagContext();
  const application = EnvService.application;
  const { sidebar } = applicationConfigStore.applicationConfig;

  // Feature flag checks
  const affiliationFeatureEnabled = isFeatureEnabled(
    FeatureFlagNames.DisableAffiliation,
  );

  const affiliateId = userStore.currentUser?.affiliateId;

  const enablePlaybook = useGetFeatureFlag('enable-playbook', false, {
    env: application,
  });

  const enableCommunityStats = useGetFeatureFlag(
    'enable-community-stats',
    false,
    { env: application },
  );

  useEffect(() => {
    featureFlag.setAttributes({
      userId: userStore.currentUser.userId,
      email: userStore.currentUser.email,
      firstName: userStore.currentUser.firstName,
      lastName: userStore.currentUser.lastName,
      country: userStore.currentUser.country as CountryCodeType,
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const routeMap: Partial<Record<Pages, RouteObject>> = {
    [Pages.Overview]: {
      path: PagePath.Overview,
      element: <Overview />,
      loader: overviewLoader,
    },
    [Pages.Profile]: {
      path: `${PagePath.Profile}/:pid?`,
      element: <Portfolio />,
    },
    [Pages.Contests]: {
      path: PagePath.Contests,
      element: <ContestCategoryPage />,
    },
    [Pages.Orders]: {
      path: PagePath.Orders,
      element: <Purchases />,
      children: [
        {
          index: true,
          loader: redirectTo('/purchases/orders'),
        },
        {
          path: 'coupons',
          element: <PurchasesCoupons />,
        },
        {
          path: 'orders',
          element: <PurchasesOrders />,
        },
        {
          path: 'withdrawals',
          element: <PurchasesWithdrawals />,
        },
      ],
    },
    [Pages.Affiliation]: createAffiliationRoute(
      affiliationFeatureEnabled,
      !!affiliateId,
      <AffiliationRouteWrapper />,
      <AffiliationWelcome />,
    ),
    [Pages.Achievements]: {
      path: PagePath.Achievements,
      element: <Achievements />,
    },
    // Map static pages
    [Pages.Autochartist]: {
      path: PagePath.Autochartist,
      element: (
        <StaticPage src={StaticPagesUrls['autochartist'][application]} />
      ),
    },
    [Pages.AssetsSpecifications]: {
      path: PagePath.AssetsSpecifications,
      element: (
        <StaticPage
          src={StaticPagesUrls['assets-specifications'][application]}
        />
      ),
    },
    [Pages.HubDownloads]: {
      path: PagePath.HubDownloads,
      element: (
        <StaticPage src={StaticPagesUrls['hub-downloads'][application]} />
      ),
    },
    [Pages.HubVod]: {
      path: PagePath.HubVod,
      element: <StaticPage src={StaticPagesUrls['hub-vod'][application]} />,
    },
    [Pages.TradingPlan]: {
      path: PagePath.TradingPlan,
      element: (
        <StaticPage src={StaticPagesUrls['trading-plan'][application]} />
      ),
    },
    [Pages.AffiliateKit]: {
      path: PagePath.AffiliateKit,
      element: <AffiliateKitPage />,
    },
    [Pages.PerformanceCoach]: {
      path: PagePath.PerformanceCoach,
      element: (
        <StaticPage src={StaticPagesUrls['performance-coach'][application]} />
      ),
    },
  };

  // Generate navbar routes (previously sidebar routes)
  const navRoutes = getNavigationRoutes(sidebar, routeMap);

  // Combine all routes
  const allRoutes = [
    ...navRoutes,
    ...(enablePlaybook
      ? [
          {
            path: PagePath.Playbook,
            children: [
              { index: true, element: <Playbook /> },
              {
                path: ':playbookId/*',
                element: (
                  <PlaybookDetailPage userId={userStore.currentUser?.userId} />
                ),
              },
            ],
          },
        ]
      : []),
    {
      path: PagePath.AffiliateProgram,
      element: <AffiliateProgramPage />,
    },
    {
      path: PagePath.PerformanceCoach,
      element: (
        <StaticPage src={StaticPagesUrls['performance-coach'][application]} />
      ),
    },
    ...(application === Application.THE5ERS && enableCommunityStats
      ? [
          {
            path: PagePath.CommunityStats,
            element: <CommunityStats />,
          },
        ]
      : []),
    {
      path: `${PagePath.Overview}/:tsAccountId/position/:positionId`,
      element: <Position />,
    },
    // Profile routes
    {
      path: `${PagePath.Profile}/edit`,
      element: <Profile tab="ProfileEdit" />,
    },
    {
      path: `${PagePath.Profile}/verification`,
      element: <Profile tab="Verification" />,
    },
    // Privacy Opt-Out route
    {
      path: PagePath.PrivacyOptOut,
      element: <PrivacyOptOut />,
    },
    // Contest routes
    {
      path: `${PagePath.Contests}/:id`,
      element: <NovaContestPage />,
    },
    {
      path: `${PagePath.Contests}/contest-overview/:tsAccountId`,
      element: <NovaContestDashboard />,
    },
    {
      path: '/welcome',
      element: <Welcome />,
    },
    {
      path: '/partners/:id',
      element: <Partners />,
    },
    // Coupon route
    {
      path: '/coupons/:org/:name',
      loader: ({ params }: LoaderFunctionArgs) => {
        const { org, name } = params as { org?: string; name?: string };
        if (!org || !name) {
          return redirect('/purchases/coupons');
        }
        return redirect(`/purchases/coupons/${org}/${name}`);
      },
    },
    {
      path: '/paysafe-checkout/:sessionId',
      element: <PaySafeCheckoutPage />,
    },
    {
      path: `/${PagePath.AfterPurchase}/:snapshotId`,
      element: <AfterPurchase />,
    },
    {
      path: '/release-preview/:id',
      element: <ReleasePreviewPage />,
    },
    // Purchases route (primary route)
    {
      path: '/purchases',
      element: <Purchases />,
      children: [
        {
          index: true,
          loader: redirectTo('/purchases/orders'),
        },
        {
          path: 'coupons/:org?/:name?',
          element: <PurchasesCoupons />,
        },
        {
          path: 'orders',
          element: <PurchasesOrders />,
        },
        {
          path: 'withdrawals',
          element: <PurchasesWithdrawals />,
        },
      ],
    },
    // Catch-all route
    {
      path: '*',
      loader: redirectTo('/'),
    },
  ];

  return createBrowserRouter(
    [
      {
        path: '/logout',
        element: <LogoutPage />,
      },
      {
        path: '/',
        loader: anonymousUserGuardLoader,
        element: (
          <BlacklistGuard>
            <Layout />
          </BlacklistGuard>
        ),
        children: [
          {
            index: true,
            loader: createInitialRedirectLoader(
              PagePath.Overview,
              PagePath.Contests,
            ),
          },
          ...allRoutes,
        ],
        errorElement: <ErrorPage />,
      },
      // Trade-talk routes with transparent layout variant
      {
        path: PagePath.TradeTalk,
        element: (
          <BlacklistGuard>
            <Layout variant="trade-talk" />
          </BlacklistGuard>
        ),
        children: [
          {
            index: true,
            element: <TradeTalks />,
          },
          {
            path: ':id',
            element: <Article />,
          },
        ],
        errorElement: <ErrorPage />,
      },
      {
        path: PagePath.Charts,
        element: (
          <BlacklistGuard>
            <Layout variant="trade-talk" />
          </BlacklistGuard>
        ),
        children: [
          {
            index: true,
            element: <Charts />,
          },
        ],
        errorElement: <ErrorPage />,
      },
      {
        path: PagePath.Blacklisted,
        element: <Layout />,
        children: [{ index: true, element: <BlacklistPage /> }],
        errorElement: <ErrorPage />,
      },
      {
        path: '/purchase',
        element: (
          <BlacklistGuard>
            <PurchasePage />
          </BlacklistGuard>
        ),
      },
      {
        path: '/quick-purchase',
        element: <QuickPurchasePage />,
        errorElement: <ErrorPage />,
      },
      ...(application === Application.TTP
        ? [
            {
              path: '/overview/:id/*',
              element: <Layout />,
              children: [{ index: true, element: <DashboardContainer /> }],
              errorElement: <ErrorPage />,
            },
          ]
        : [
            {
              path: '/overview/:sourceId/*',
              element: <Layout />,
              children: [{ index: true, element: <AccountDashboard /> }],
              errorElement: <ErrorPage />,
            },
            {
              path: '/overview-old/:id/*',
              element: <Layout />,
              children: [{ index: true, element: <DashboardContainer /> }],
              errorElement: <ErrorPage />,
            },
          ]),
    ],
    {
      basename: basename,
    },
  );
};
